import { User } from "../types";

const TOKEN_KEY = "accessToken";
const USER_KEY = "userData";

export function getAccessToken(): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && isTokenExpired(token)) {
      logout();
      return null;
    }
    return token;
  } catch (error) {
    console.error("Error getting access token:", error);
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  try {
    const payloadBase64 = token.split(".")[1];
    if (!payloadBase64) return true;
    const decodedJson = atob(payloadBase64);
    const payload = JSON.parse(decodedJson);
    const exp = payload.exp;
    if (!exp) return false; // If no exp, assume it doesn't expire
    // Check if expired (exp is in seconds, Date.now() in ms)
    return Date.now() >= exp * 1000;
  } catch (e) {
    return true; // If decoding fails, assume expired/invalid
  }
}

export function getTimeUntilExpiry(token: string): number | null {
  try {
    const payloadBase64 = token.split(".")[1];
    if (!payloadBase64) return null;
    const decodedJson = atob(payloadBase64);
    const payload = JSON.parse(decodedJson);
    const exp = payload.exp;
    if (!exp) return null;
    return Math.max(0, exp * 1000 - Date.now());
  } catch (e) {
    return null;
  }
}

export function getUserData(): User | null {
  try {
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error getting user data:", error);
    return null;
  }
}

export function setAccessToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch (error) {
    console.error("Error setting access token:", error);
  }
}

export function setUserData(user: User | null): void {
  try {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
  } catch (error) {
    console.error("Error setting user data:", error);
  }
}

export async function logout(): Promise<void> {
  try {
    // Clear token and user from localStorage
    setAccessToken(null);
    setUserData(null);

    // Call backend logout endpoint
    try {
      // Note: We'll use a simple fetch here to avoid circular dependencies with config/api
      // The backend URL is usually the same or relative
      await fetch("/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      // Ignore errors - logout should work even if backend is unavailable
    }
  } catch (error) {
    // Ensure token is cleared even if something fails
    setAccessToken(null);
  }
}

